const PCM_SAMPLE_RATE = 16_000;

function writeAscii(view: DataView, offset: number, value: string) {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

export function resampleMonoChannels(
  channels: ReadonlyArray<Float32Array>,
  inputSampleRate: number,
  outputSampleRate = PCM_SAMPLE_RATE
) {
  if (channels.length === 0 || inputSampleRate <= 0 || outputSampleRate <= 0) {
    throw new Error("invalid_audio_shape");
  }
  const sourceLength = channels[0]?.length || 0;
  if (sourceLength === 0 || channels.some((channel) => channel.length !== sourceLength)) {
    throw new Error("invalid_audio_shape");
  }

  const outputLength = Math.max(1, Math.round((sourceLength * outputSampleRate) / inputSampleRate));
  const ratio = inputSampleRate / outputSampleRate;
  const output = new Int16Array(outputLength);
  for (let outputIndex = 0; outputIndex < outputLength; outputIndex += 1) {
    const sourcePosition = outputIndex * ratio;
    const leftIndex = Math.min(sourceLength - 1, Math.floor(sourcePosition));
    const rightIndex = Math.min(sourceLength - 1, leftIndex + 1);
    const mix = sourcePosition - leftIndex;
    let sample = 0;
    for (const channel of channels) {
      sample += channel[leftIndex] * (1 - mix) + channel[rightIndex] * mix;
    }
    sample = Math.max(-1, Math.min(1, sample / channels.length));
    output[outputIndex] = sample < 0 ? Math.round(sample * 0x8000) : Math.round(sample * 0x7fff);
  }
  return output;
}

export function encodePcm16Wav(samples: Int16Array, sampleRate = PCM_SAMPLE_RATE) {
  const bytes = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(bytes);
  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, "data");
  view.setUint32(40, samples.length * 2, true);
  samples.forEach((sample, index) => view.setInt16(44 + index * 2, sample, true));
  return bytes;
}

function getAudioContextClass() {
  if (typeof window === "undefined") return undefined;
  return (
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  );
}

/**
 * Whether this browser exposes the API the WAV conversion needs at all.
 *
 * This is deliberately a weak claim: it says the constructor and
 * `decodeAudioData` exist, not that decoding this browser's own recording will
 * succeed. Safari can pass this check and still fail on the container its
 * MediaRecorder produces, which is a known open problem — see
 * `PronunciationAudioError`, which is how that failure reaches telemetry
 * instead of disappearing.
 */
export function canEncodePronunciationWav() {
  const AudioContextClass = getAudioContextClass();
  return (
    typeof AudioContextClass === "function" &&
    typeof AudioContextClass.prototype?.decodeAudioData === "function"
  );
}

export type PronunciationAudioFailureCode =
  | "unsupported_browser"
  | "decode_failed"
  | "invalid_audio_shape";

/**
 * A conversion failure on the learner's device, as opposed to our pronunciation
 * service being unreachable. The two deserve different wording and different
 * dashboards, so they get different error types.
 *
 * Written with explicit fields rather than TypeScript parameter properties so the
 * module stays runnable under `node --experimental-strip-types`, which is the only
 * way this file's logic can be executed outside a full Jest run.
 */
export class PronunciationAudioError extends Error {
  readonly code: PronunciationAudioFailureCode;
  readonly recordingMimeType: string;

  constructor(
    code: PronunciationAudioFailureCode,
    recordingMimeType: string,
    options?: ErrorOptions
  ) {
    super(`Pronunciation audio conversion failed: ${code}`, options);
    this.name = "PronunciationAudioError";
    this.code = code;
    this.recordingMimeType = recordingMimeType;
  }
}

export async function recordingBlobToPronunciationWav(recording: Blob) {
  const AudioContextClass = getAudioContextClass();
  if (!AudioContextClass) {
    throw new PronunciationAudioError("unsupported_browser", recording.type);
  }
  const context = new AudioContextClass();
  try {
    let decoded: AudioBuffer;
    try {
      decoded = await context.decodeAudioData(await recording.arrayBuffer());
    } catch (error) {
      // Reached when the browser cannot read the container its own recorder
      // wrote. Carrying the MIME type is the whole point: it is the one piece of
      // evidence needed to work out which browsers to fix.
      throw new PronunciationAudioError("decode_failed", recording.type, { cause: error });
    }
    const channels = Array.from({ length: decoded.numberOfChannels }, (_, channel) =>
      decoded.getChannelData(channel)
    );
    try {
      const samples = resampleMonoChannels(channels, decoded.sampleRate);
      return new Blob([encodePcm16Wav(samples)], { type: "audio/wav" });
    } catch (error) {
      throw new PronunciationAudioError("invalid_audio_shape", recording.type, { cause: error });
    }
  } finally {
    await context.close().catch(() => undefined);
  }
}
